import React, { useRef, useEffect, useState } from 'react';
import { PRODUCT } from '../product';

// Login backdrop: a muted, looping hero video that covers the screen.
//
// Each product has its own clip. TagAlong Pro uses pro-login.mp4 and, per the
// request, plays with NO sound at all — so the intro-sound track and its mute
// button are skipped entirely for Pro rather than just muted.
const IS_PRO = PRODUCT.key === 'pro';
const VIDEO = `${process.env.PUBLIC_URL || ''}/${IS_PRO ? 'pro-login' : 'tagalong-login'}.mp4`;
const SOUND = `${process.env.PUBLIC_URL || ''}/tagalong-sound.m4a`;
const SILENT = IS_PRO; // no intro sound, no mute toggle for Pro

export default function LoginBackground() {
  const videoRef = useRef(null);
  const audioRef = useRef(null);
  const startedRef = useRef(false); // sound has begun this page load (play it once)
  const [muted, setMuted] = useState(false);

  // keep the background video muted + playing
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = true; v.defaultMuted = true;
    const p = v.play();
    if (p && p.catch) p.catch(() => {});
  }, []);

  // play the intro sound once on load. Browsers block autoplay-with-sound until
  // the user interacts, so if the immediate attempt is refused we start it on
  // the very first click/keypress. It plays a single time (no loop).
  useEffect(() => {
    if (SILENT) return undefined; // Pro: no intro sound at all
    const a = audioRef.current;
    if (!a) return undefined;
    a.volume = 0.3; // keep the intro sound soft, not blaring
    const gestures = ['pointerdown', 'keydown', 'touchstart', 'click'];
    const cleanup = () => gestures.forEach((g) => window.removeEventListener(g, onGesture));
    const start = () => {
      if (startedRef.current) return;
      startedRef.current = true;
      a.currentTime = 0;
      const pr = a.play();
      if (pr && pr.catch) pr.catch(() => { startedRef.current = false; }); // let a later gesture retry
      else cleanup();
    };
    const onGesture = () => { start(); if (startedRef.current) cleanup(); };

    start(); // attempt immediately
    gestures.forEach((g) => window.addEventListener(g, onGesture, { passive: true }));
    return cleanup;
  }, []);

  const toggleMute = () => {
    const a = audioRef.current;
    if (!a) return;
    // first tap while still blocked: start it (unmuted)
    if (!startedRef.current) {
      startedRef.current = true;
      a.muted = false; setMuted(false);
      a.currentTime = 0;
      a.play().catch(() => { startedRef.current = false; });
      return;
    }
    a.muted = !a.muted;
    setMuted(a.muted);
  };

  return (
    <>
      <div aria-hidden="true" style={{ position: 'absolute', inset: 0, overflow: 'hidden', background: '#140627', zIndex: 0 }}>
        <video
          ref={videoRef}
          src={VIDEO}
          autoPlay
          loop
          muted
          playsInline
          preload="auto"
          style={{ position: 'absolute', top: '50%', left: '50%', minWidth: '100%', minHeight: '100%', width: 'auto', height: 'auto', transform: 'translate(-50%, -50%)', objectFit: 'cover' }}
        />
      </div>

      {/* Pro plays silently, so there's no audio element and no mute button. */}
      {!SILENT && <audio ref={audioRef} src={SOUND} preload="auto" />}

      {/* mute / unmute the intro sound */}
      {!SILENT && (
      <button
        onClick={toggleMute}
        title={muted ? 'Unmute sound' : 'Mute sound'}
        aria-label={muted ? 'Unmute sound' : 'Mute sound'}
        style={{
          position: 'fixed', bottom: '20px', right: '20px', zIndex: 50,
          width: '46px', height: '46px', borderRadius: '50%', cursor: 'pointer',
          border: '1px solid rgba(255,255,255,0.35)', background: 'rgba(10,6,20,0.55)',
          color: 'white', fontSize: '20px', backdropFilter: 'blur(6px)',
          boxShadow: '0 6px 18px rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        {muted ? '🔇' : '🔊'}
      </button>
      )}
    </>
  );
}
